import Transaction from '@/models/Transaction';
import { Dispatch, SetStateAction, useEffect, useState, useRef, useImperativeHandle, useId, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import TransactionTypeFilter from './filters/TransactionTypeFilter';
import PaymentMethodFilter from './filters/PaymentMethodFilter';
import AmountFilter from './filters/AmountFilter';
import DateFilter from './filters/DateFilter';
import ContactNameFilter from './filters/ContactNameFilter';
import CurrencyFilter from './filters/CurrencyFilter';
import TransactionStatusFilter from './filters/TransactionStatusFilter';
import { TransactionStatus, transactionStatus } from './futureTransactions';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import styles from './FiltersArea.module.scss';
import { Resettable } from '@/models/Resettable';
import { getDefaultTransactionsDateRange } from '@/helpers/dateHelper';
import useUserPreference from '@/hooks/useUserPreference';
import useDeviceDetection from '@/hooks/useDeviceDetection';
import { ANALYTICS_EVENTS } from '@/helpers/analyticsEvents';
import { usePostHogIdentification } from '@/hooks/usePostHogIdentification';

export interface AmountRange {
  from: string;
  to: string;
}

interface FiltersAreaProps {
  transactions: Transaction[];
  setFilteredTransactions: Dispatch<SetStateAction<Transaction[]>>;
  /**
   * HAT-1470: the filters as a test, for rows the board does not show but the sheet carries
   * (the commission lines), so the export holds only what was filtered for.
   */
  setRowFilter?: Dispatch<SetStateAction<(transaction: Transaction) => boolean>>;
  onDateRangeChange: (from?: string, to?: string) => void;
  dateRange?: { start?: string; end?: string };
  selectedContactName: string | null;
  setSelectedContactName: (contactName: string | null) => void;
  ref?: Ref<{ resetFilters: () => void }>;
  /**
   * HAT-1212: העמוד באמצע refetch. עובר ל-DateFilter וממנו ל-OptionsRangeFilter,
   * כדי שחצי הניווט של הלוח לא ייקלטו בזמן שהעמוד עדיין מסדר את הנתונים החדשים.
   */
  isBusy?: boolean;
  /** HAT-1203: the wide layout has «תנועות עתידיות», so it can filter by whether a row has happened yet. */
  showStatusFilter?: boolean;
}

export interface FilterState {
  kinds: string[];
  paymentMethods: string[];
  currencies: string[];
  amount?: AmountRange;
  /** Empty means every row; `future` also admits projected rows, which are future rows too. */
  statuses: TransactionStatus[];
}

const matchesStatus = (transaction: Transaction, statuses: TransactionStatus[]): boolean => {
  if (statuses.length === 0) return true;
  const status = transactionStatus(transaction);
  return statuses.includes(status) || (status !== 'current' && statuses.includes('future'));
};

const matchesKinds = (transaction: Transaction, kinds: string[]): boolean => {
  return kinds.length === 0 || kinds.includes(transaction.kind);
};

const matchesPaymentMethods = (transaction: Transaction, paymentMethods: string[]): boolean => {
  return paymentMethods.length === 0 || paymentMethods.includes(transaction.paymentMethod);
};

const matchesCurrency = (transaction: Transaction, currencies: string[]): boolean => {
  return currencies.length === 0 || currencies.includes(transaction.currency);
};

/*
 * Signs are ignored, and a foreign movement is matched on its ILS figure as well - the same two
 * rules the board's search box uses, because they are how the board presents an amount in the first
 * place: an expense reads as -20.00 and a coordinator looks it up as 20, and a foreign movement
 * shows its shekel value beside the original. Comparing the raw signed amount instead meant an exact
 * amount typed straight off the screen matched nothing at all (HAT-1155).
 */
export const matchesAmountRange = (transaction: Transaction, amountRange?: AmountRange): boolean => {
  if (!amountRange) return true;

  const bound = (value: string): number | null => {
    const parsed = value ? parseFloat(value) : NaN;
    return Number.isNaN(parsed) ? null : Math.abs(parsed);
  };

  const from = bound(amountRange.from);
  const to = bound(amountRange.to);
  if (from === null && to === null) return true;

  const within = (amount: number): boolean => {
    const magnitude = Math.abs(amount);
    return (from === null || magnitude >= from) && (to === null || magnitude <= to);
  };

  return within(transaction.amount) || (transaction.ilsAmount != null && within(transaction.ilsAmount));
};

const matchesContactName = (transaction: Transaction, contactName: string | null): boolean => {
  return !contactName || transaction.contactName === contactName;
};

export const matchesFilters = (transaction: Transaction, filterState: FilterState, contactName: string | null): boolean =>
  matchesKinds(transaction, filterState.kinds) &&
  matchesPaymentMethods(transaction, filterState.paymentMethods) &&
  matchesCurrency(transaction, filterState.currencies) &&
  matchesAmountRange(transaction, filterState.amount) &&
  matchesStatus(transaction, filterState.statuses) &&
  matchesContactName(transaction, contactName);

const FiltersArea = ({
  transactions,
  setFilteredTransactions,
  setRowFilter,
  onDateRangeChange,
  dateRange = getDefaultTransactionsDateRange(),
  selectedContactName,
  setSelectedContactName,
  ref,
  isBusy,
  showStatusFilter = false,
}: FiltersAreaProps) => {
  const { t } = useTranslation();
  const { captureEvent } = usePostHogIdentification();
  const [filterState, setFilterState] = useState<FilterState>({
    kinds: [],
    paymentMethods: [],
    currencies: [],
    amount: undefined,
    statuses: [],
  });
  const defaultRange = getDefaultTransactionsDateRange();
  const isDateFiltered = dateRange.start !== defaultRange.start || dateRange.end !== defaultRange.end;
  const activeFilterCount = [
    isDateFiltered,
    filterState.kinds.length > 0,
    filterState.paymentMethods.length > 0,
    filterState.currencies.length > 0,
    Boolean(filterState.amount?.from || filterState.amount?.to),
    filterState.statuses.length > 0,
    selectedContactName !== null,
  ].filter(Boolean).length;
  const [storedExpanded, setStoredExpanded] = useUserPreference('transactionsFiltersExpanded');
  const { isSidebarDocked } = useDeviceDetection();
  const filtersId = useId();

  const transactionTypeFilterRef = useRef<Resettable>(null);
  const statusFilterRef = useRef<Resettable>(null);
  const paymentMethodFilterRef = useRef<Resettable>(null);
  const currencyFilterRef = useRef<Resettable>(null);
  const amountFilterRef = useRef<Resettable>(null);
  const dateFilterRef = useRef<Resettable>(null);
  const enableUsTransactions = import.meta.env.VITE_APP_ENABLE_US_TRANSACTIONS === 'true';

  const resetAllFilters = () => {
    setFilterState({
      kinds: [],
      paymentMethods: [],
      currencies: [],
      amount: undefined,
      statuses: [],
    });

    transactionTypeFilterRef.current?.reset();
    statusFilterRef.current?.reset();
    paymentMethodFilterRef.current?.reset();
    currencyFilterRef.current?.reset();
    amountFilterRef.current?.reset();
    setSelectedContactName(null);
    dateFilterRef.current?.reset();
    onDateRangeChange(defaultRange.start, defaultRange.end);
  };

  useEffect(() => {
    const matches = (transaction: Transaction) => matchesFilters(transaction, filterState, selectedContactName);
    setFilteredTransactions(transactions.filter(matches));
    // A function in state is read as an updater, hence the wrapping arrow.
    setRowFilter?.(() => matches);
  }, [filterState, selectedContactName, transactions, setFilteredTransactions, setRowFilter]);

  const filtersReadyRef = useRef(false);
  useEffect(() => {
    if (!filtersReadyRef.current) {
      filtersReadyRef.current = true;
      return;
    }
    if (activeFilterCount === 0) return;
    const filter = selectedContactName
      ? 'contact_name'
      : isDateFiltered
        ? 'date'
        : filterState.kinds.length > 0
          ? 'kind'
          : filterState.paymentMethods.length > 0
            ? 'payment_method'
            : filterState.currencies.length > 0
              ? 'currency'
              : filterState.amount?.from || filterState.amount?.to
                ? 'amount'
                : 'unknown';
    captureEvent(ANALYTICS_EVENTS.TRANSACTIONS_FILTER_USED, { filter });
    // captureEvent is recreated each render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState, selectedContactName, isDateFiltered, activeFilterCount]);

  useImperativeHandle(ref, () => ({
    resetFilters: resetAllFilters,
  }));

  const isExpanded = storedExpanded === null ? isSidebarDocked : storedExpanded === 'true';
  const toggleLabel = t(isExpanded ? 'hideTransactionFilters' : 'showTransactionFilters');

  const collapseToggle = (
    <button
      type="button"
      className={styles.collapseToggle}
      onClick={() => setStoredExpanded(String(!isExpanded))}
      aria-expanded={isExpanded}
      aria-controls={filtersId}
      aria-label={`${toggleLabel}${activeFilterCount ? `, ${t('activeTransactionFilters', { count: activeFilterCount })}` : ''}`}
      title={toggleLabel}
      data-active={activeFilterCount > 0}
    >
      <span>{t('transactionFilters')}</span>
      {activeFilterCount > 0 && (
        <span className={styles.activeCount} aria-hidden="true">
          {activeFilterCount}
        </span>
      )}
      <ExpandMoreIcon fontSize="small" className={styles.collapseChevron} />
    </button>
  );

  return (
    <div className={styles.filtersArea}>
      <div className={styles.filtersContainer} data-board-filters>
        <DateFilter ref={dateFilterRef} onDateRangeChange={onDateRangeChange} dateRange={dateRange} isBusy={isBusy} />
        {collapseToggle}
        <div id={filtersId} className={styles.filterOptions} hidden={!isExpanded}>
          <TransactionTypeFilter ref={transactionTypeFilterRef} setFilterState={setFilterState} />
          {showStatusFilter && <TransactionStatusFilter ref={statusFilterRef} setFilterState={setFilterState} />}
          <PaymentMethodFilter ref={paymentMethodFilterRef} setFilterState={setFilterState} />
          {enableUsTransactions && <CurrencyFilter ref={currencyFilterRef} setFilterState={setFilterState} />}
          <AmountFilter ref={amountFilterRef} setFilterState={setFilterState} />
          <ContactNameFilter selectedContactName={selectedContactName} setSelectedContactName={setSelectedContactName} />
        </div>
        {activeFilterCount > 0 && (
          <span className={styles.activeFiltersNote}>
            <button type="button" className="body-md-link text-(--font-body-1)" onClick={resetAllFilters}>
              {t('resetFilters')}
            </button>
          </span>
        )}
      </div>
    </div>
  );
};

export default FiltersArea;
